import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { del, errorMessage, get, patch, post } from '../api/client';
import type { LeadDetail, LeadRow, LeadsPage, LeadStatus, LeadsSummary } from '../api/types';
import { useMe } from '../auth';
import { IconArrowLeft, IconDownload, IconMail, IconPhone, IconTrash } from '../components/icons';
import { formatDate, PageHead, Spinner, timeAgo, useFeedback } from '../components/ui';
import { tk, type Translator, useT } from '../i18n';
import { BRAND, SITE } from '../site';

/** Las etiquetas se traducen con t() al mostrarlas. */
export const STATUS: Record<LeadStatus, { label: string; tone: string }> = {
  NEW: { label: tk('Nuevo'), tone: 'red' },
  IN_PROGRESS: { label: tk('En gestión'), tone: 'info' },
  ANSWERED: { label: tk('Respondido'), tone: 'ok' },
  ARCHIVED: { label: tk('Archivado'), tone: '' },
  SPAM: { label: tk('Spam'), tone: 'warn' },
};

const FILTERS: { id: string; label: string }[] = [
  { id: 'inbox', label: tk('Bandeja') },
  { id: 'NEW', label: tk('Nuevos') },
  { id: 'IN_PROGRESS', label: tk('En gestión') },
  { id: 'ANSWERED', label: tk('Respondidos') },
  { id: 'ARCHIVED', label: tk('Archivados') },
  { id: 'SPAM', label: tk('Spam') },
  { id: 'all', label: tk('Todos') },
];

/** "Contacto", "Dealer", "Distribuidor" (cada dashboard ve solo los mensajes de su marca; el tipo de socio viene de la API). */
const originLabel = (l: LeadRow, t: Translator['t']) => (l.form === 'contact' ? t('Contacto') : (l.partnerTypeLabel ?? t('Socio')));

/** Fila de la bandeja (también se usa en la portada). */
export function LeadItem({ lead, to, active }: { lead: LeadRow; to: string; active?: boolean }) {
  const { t } = useT();
  return (
    <Link to={to} className={`lead-item ${active ? 'active' : ''}`} aria-current={active ? 'true' : undefined}>
      <span className="top">
        {lead.status === 'NEW' && <span className="dot" aria-label={t('Nuevo')} />}
        <span className="who">{lead.company || lead.name || lead.email}</span>
        <span className="when">{timeAgo(lead.createdAt)}</span>
      </span>
      <span className="faint meta">
        {originLabel(lead, t)} · {lead.email}
        {lead.crmStatus === 'FAILED' && <span className="badge red" style={{ marginLeft: 6 }}>{t('No llegó al CRM')}</span>}
      </span>
      {lead.snippet && <span className="snippet">{lead.snippet}</span>}
    </Link>
  );
}

export function Leads() {
  const { t } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('estado') ?? 'inbox';
  const [q, setQ] = useState(params.get('q') ?? '');
  const page = Number(params.get('pagina') ?? 1);

  // Búsqueda con una pequeña espera mientras se escribe.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') === q) return;
      const next = new URLSearchParams(params);
      if (q) next.set('q', q);
      else next.delete('q');
      next.delete('pagina');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [q, params, setParams]);

  const query = new URLSearchParams({ status, page: String(page), pageSize: '25', ...(params.get('q') && { q: params.get('q')! }) });
  const summary = useQuery({ queryKey: ['leads', 'summary'], queryFn: () => get<LeadsSummary>('/admin/leads/summary') });
  const list = useQuery({
    queryKey: ['leads', 'list', query.toString()],
    queryFn: () => get<LeadsPage>(`/admin/leads?${query}`),
    placeholderData: keepPreviousData,
  });

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('pagina');
    setParams(next);
  };

  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;
  const search = params.toString() ? `?${params}` : '';
  // Un enlace que abre el navegador no lleva encabezados: la marca del dashboard va en ?dashboard=.
  const exportUrl = `/api/admin/leads/export?${new URLSearchParams({ status, ...(params.get('q') && { q: params.get('q')! }), dashboard: SITE })}`;
  const description =
    SITE === 'phelon-moore'
      ? t('Lo que llega por el formulario de socios (distribuidores y concesionarios) de la landing.')
      : t('Lo que llega por los formularios de contacto y de socios del sitio de {brand}.', { brand: BRAND.name });

  return (
    <>
      <PageHead kicker={t('Formularios')} title={t('Mensajes')} description={description}>
        <a className="btn" href={exportUrl}>
          <IconDownload /> {t('Exportar CSV')}
        </a>
      </PageHead>

      <div className={`leads ${id ? 'has-selection' : ''}`}>
        <div className="filters">
          <div className="segmented" role="group" aria-label={t('Estado')}>
            {FILTERS.map((f) => (
              <button key={f.id} aria-pressed={status === f.id} onClick={() => setFilter('estado', f.id === 'inbox' ? '' : f.id)}>
                {t(f.label)}
                {f.id === 'NEW' && summary.data?.new ? ` (${summary.data.new})` : ''}
              </button>
            ))}
          </div>
          <input
            className="input"
            type="search"
            placeholder={t('Buscar nombre, correo, empresa…')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('Buscar')}
          />
        </div>

        <div className="inbox">
          <div className="panel inbox-list" style={{ overflow: 'hidden' }}>
            {list.isPending ? (
              <div className="panel-pad">
                <Spinner />
              </div>
            ) : list.data!.items.length === 0 ? (
              <div className="empty-state">{t('No hay mensajes con este filtro.')}</div>
            ) : (
              list.data!.items.map((l) => <LeadItem key={l.id} lead={l} to={`/mensajes/${l.id}${search}`} active={l.id === id} />)
            )}
            {pages > 1 && (
              <div className="row between panel-pad" style={{ paddingTop: 12, paddingBottom: 12 }}>
                <button className="btn btn-sm" disabled={page <= 1} onClick={() => setFilter('pagina', String(page - 1))}>
                  {t('Anterior')}
                </button>
                <span className="faint">{t('Página {page} de {pages}', { page, pages })}</span>
                <button className="btn btn-sm" disabled={page >= pages} onClick={() => setFilter('pagina', String(page + 1))}>
                  {t('Siguiente')}
                </button>
              </div>
            )}
          </div>

          {id ? (
            <LeadPanel key={id} id={id} backTo={`/mensajes${search}`} onDeleted={() => navigate(`/mensajes${search}`)} />
          ) : (
            <div className="panel empty-state inbox-empty">{t('Elige un mensaje para verlo.')}</div>
          )}
        </div>
      </div>
    </>
  );
}

function LeadPanel({ id, backTo, onDeleted }: { id: string; backTo: string; onDeleted: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const {
    data: lead,
    isPending,
    error,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => (await get<{ lead: LeadDetail }>(`/admin/leads/${id}`)).lead,
  });
  const [notes, setNotes] = useState('');
  useEffect(() => setNotes(lead?.notes ?? ''), [lead?.notes]);

  const refresh = (l: LeadDetail) => {
    qc.setQueryData(['lead', id], l);
    void qc.invalidateQueries({ queryKey: ['leads'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
  const update = async (body: { status?: LeadStatus; notes?: string }, ok: string) => {
    try {
      refresh((await patch<{ lead: LeadDetail }>(`/admin/leads/${id}`, body)).lead);
      toast(ok);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  if (isPending)
    return (
      <div className="panel panel-pad">
        <Spinner />
      </div>
    );
  if (error || !lead) return <div className="notice error">{errorMessage(error)}</div>;

  const subject = encodeURIComponent(`${lead.siteLabel}: ${lead.form === 'contact' ? t('tu mensaje') : t('tu solicitud')}`);

  return (
    <div className="panel panel-pad stack">
      <Link to={backTo} className="btn btn-sm btn-ghost only-narrow" style={{ justifySelf: 'start', marginLeft: -8 }}>
        <IconArrowLeft /> {t('Bandeja')}
      </Link>
      <div className="row between row-wrap" style={{ alignItems: 'flex-start' }}>
        <div>
          <p className="kicker" style={{ marginBottom: 4 }}>
            {lead.siteLabel} · {lead.formLabel}
            {lead.partnerTypeLabel ? ` · ${lead.partnerTypeLabel}` : ''}
          </p>
          <h2 style={{ fontSize: 22, margin: 0 }}>{lead.company || lead.name || lead.email}</h2>
          <p className="faint" style={{ margin: '2px 0 0' }}>
            {formatDate(lead.createdAt)}
            {lead.company && lead.name ? ` · ${lead.name}` : ''}
          </p>
        </div>
        <span className={`badge ${STATUS[lead.status].tone}`}>{t(STATUS[lead.status].label)}</span>
      </div>

      <div className="row row-wrap">
        <a className="btn btn-primary" href={`mailto:${lead.email}?subject=${subject}`}>
          <IconMail /> {t('Responder a {email}', { email: lead.email })}
        </a>
        {lead.phone && (
          <a className="btn" href={`tel:${lead.phone.replace(/\s/g, '')}`}>
            <IconPhone /> {lead.phone}
          </a>
        )}
      </div>

      <div>
        <span className="field-label" style={{ marginBottom: 6, display: 'block' }}>
          {t('Estado')}
        </span>
        <div className="segmented" role="group" aria-label={t('Cambiar estado')}>
          {(Object.keys(STATUS) as LeadStatus[]).map((s) => (
            <button key={s} aria-pressed={lead.status === s} onClick={() => void update({ status: s }, t('Marcado como «{status}»', { status: t(STATUS[s].label) }))}>
              {t(STATUS[s].label)}
            </button>
          ))}
        </div>
      </div>

      <div className="kv">
        {lead.fields.map((f) => (
          <FieldRow key={f.key} label={f.label} value={f.value} />
        ))}
      </div>

      <div className="field">
        <label htmlFor="lead-notes">{t('Notas internas')}</label>
        <textarea id="lead-notes" className="textarea" rows={3} value={notes} placeholder={t('Solo las ve el equipo')} onChange={(e) => setNotes(e.target.value)} />
        {notes !== lead.notes && (
          <div>
            <button className="btn btn-sm" onClick={() => void update({ notes }, t('Notas guardadas'))}>
              {t('Guardar notas')}
            </button>
          </div>
        )}
      </div>

      <details>
        <summary className="faint" style={{ cursor: 'pointer' }}>
          {t('Datos del envío')}
        </summary>
        <div className="kv" style={{ marginTop: 8, fontSize: 13 }}>
          <FieldRow
            label={t('Aceptó los términos')}
            value={lead.consentAt ? t('{date} — «{text}»', { date: formatDate(lead.consentAt), text: String(lead.consentText) }) : t('No aplica')}
          />
          <FieldRow label="IP" value={lead.ip ?? '—'} />
          <FieldRow label={t('Página')} value={lead.pageUrl ?? '—'} />
          <FieldRow label={t('Navegador')} value={lead.userAgent ?? '—'} />
          <FieldRow label={t('Aviso por correo')} value={lead.notifiedAt ? t('Enviado {date}', { date: formatDate(lead.notifiedAt) }) : t('No se envió')} />
        </div>
      </details>

      {SITE === 'phelon-moore' && lead.form === 'apply' && lead.status !== 'SPAM' && <CrmRow lead={lead} onChange={refresh} />}

      <div className="row row-wrap">
        {!lead.notifiedAt && (
          <button
            className="btn btn-sm"
            onClick={async () => {
              try {
                refresh((await post<{ lead: LeadDetail }>(`/admin/leads/${id}/notify`)).lead);
                toast(t('Aviso enviado'));
              } catch (e) {
                toast(errorMessage(e), 'error');
              }
            }}
          >
            <IconMail /> {t('Enviar aviso por correo')}
          </button>
        )}
        {me?.role === 'ADMIN' && (
          <button
            className="btn btn-sm btn-danger"
            style={{ marginLeft: 'auto' }}
            onClick={async () => {
              if (!(await confirm(t('¿Eliminar este mensaje para siempre?'), { confirmLabel: t('Eliminar'), danger: true }))) return;
              try {
                await del(`/admin/leads/${id}`);
                void qc.invalidateQueries({ queryKey: ['leads'] });
                toast(t('Mensaje eliminado'));
                onDeleted();
              } catch (e) {
                toast(errorMessage(e), 'error');
              }
            }}
          >
            <IconTrash /> {t('Eliminar')}
          </button>
        )}
      </div>
    </div>
  );
}

/** El envío al CRM (GoHighLevel): cómo va y, si no llegó, el botón para enviarlo otra vez. */
function CrmRow({ lead, onChange }: { lead: LeadDetail; onChange: (l: LeadDetail) => void }) {
  const { t } = useT();
  const { toast } = useFeedback();
  const [busy, setBusy] = useState(false);
  const text =
    lead.crmStatus === 'SENT'
      ? lead.crmAttempts > 1
        ? t('Enviado {date} (al intento {n})', { date: formatDate(lead.crmSentAt!), n: lead.crmAttempts })
        : t('Enviado {date}', { date: formatDate(lead.crmSentAt!) })
      : lead.crmStatus === 'PENDING'
        ? lead.crmAttempts
          ? t('No respondió (intento {n} de 4). Próximo intento: {date}', { n: lead.crmAttempts, date: formatDate(lead.crmNextAt!) })
          : t('Enviando…')
        : lead.crmStatus === 'FAILED'
          ? t('No llegó después de {n} intentos', { n: lead.crmAttempts })
          : t('No se envió: el CRM no estaba configurado cuando llegó');
  const canSend = lead.crmStatus === 'FAILED' || lead.crmStatus === null;
  return (
    <div className={`notice ${lead.crmStatus === 'FAILED' ? 'error' : lead.crmStatus === 'SENT' ? 'ok' : ''}`} style={{ display: 'grid', gap: 6 }}>
      <div className="row between row-wrap">
        <span>
          <b>CRM (GoHighLevel):</b> {text}
        </span>
        {canSend && (
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const l = (await post<{ lead: LeadDetail }>(`/admin/leads/${lead.id}/crm`)).lead;
                onChange(l);
                toast(l.crmStatus === 'SENT' ? t('Enviado al CRM') : t('El CRM no respondió: se reintentará'), l.crmStatus === 'SENT' ? undefined : 'error');
              } catch (e) {
                toast(errorMessage(e), 'error');
              } finally {
                setBusy(false);
              }
            }}
          >
            {lead.crmStatus === null ? t('Enviar al CRM') : t('Reenviar al CRM')}
          </button>
        )}
      </div>
      {lead.crmError && lead.crmStatus !== 'SENT' && (
        <span className="faint" style={{ fontSize: 13 }}>
          {t('Último error: {error}', { error: lead.crmError })}
        </span>
      )}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  const isUrl = /^https?:\/\//.test(value);
  return (
    <>
      <div>{label}</div>
      <div>
        {isUrl ? (
          <a href={value} target="_blank" rel="noreferrer noopener">
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </>
  );
}
